"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { RouteAnalyticsApiResponse } from "@/types/api";
import type { UrlFilterState } from "./use-url-filters";
import { apiFetch, buildQueryString, ApiClientError, type QueryParamValue } from "@/lib/api/api-client";
import { getDefaultRouteAnalyticsRange } from "@/utils/route-analytics-utils";

function buildRouteAnalyticsQuery(filters: UrlFilterState): string {
  const params: Record<string, QueryParamValue> = {
    method: filters.methods,
    statusCode: filters.statusCodes,
    createdFrom: filters.createdFrom?.toISOString(),
    createdTo: filters.createdTo?.toISOString(),
    updatedFrom: filters.updatedFrom?.toISOString(),
    updatedTo: filters.updatedTo?.toISOString(),
    routeGroupingEnabled: true,
  };
  return buildQueryString(params);
}

function filtersKey(filters: UrlFilterState): string {
  return JSON.stringify({
    methods: filters.methods ?? [],
    statusCodes: filters.statusCodes ?? [],
    createdFrom: filters.createdFrom?.toISOString() ?? "",
    createdTo: filters.createdTo?.toISOString() ?? "",
    updatedFrom: filters.updatedFrom?.toISOString() ?? "",
    updatedTo: filters.updatedTo?.toISOString() ?? "",
  });
}

export interface UseRouteAnalyticsResult {
  data: RouteAnalyticsApiResponse | null;
  isLoading: boolean;
  error: ApiClientError | null;
  refetch: () => void;
}

export function useRouteAnalytics(
  filters: UrlFilterState,
  analyticsApiPath = "/api/analytics/routes",
): UseRouteAnalyticsResult {
  const [data, setData] = useState<RouteAnalyticsApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [refetchToken, setRefetchToken] = useState(0);
  const defaultRangeRef = useRef(getDefaultRouteAnalyticsRange());
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const hasDateWindow = Boolean(
    filters.createdFrom || filters.createdTo || filters.updatedFrom || filters.updatedTo,
  );
  const queryFilters: UrlFilterState = hasDateWindow
    ? filters
    : { ...filters, ...defaultRangeRef.current };

  const key = filtersKey(queryFilters);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    apiFetch<RouteAnalyticsApiResponse>(`${analyticsApiPath}${buildRouteAnalyticsQuery(queryFilters)}`)
      .then((result) => {
        if (!mountedRef.current) return;
        if (requestId !== requestIdRef.current) return;
        setData(result);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (!mountedRef.current) return;
        if (requestId !== requestIdRef.current) return;
        setError(err instanceof ApiClientError ? err : new ApiClientError(500, "INTERNAL_ERROR", "Request failed"));
        setIsLoading(false);
      });

    // No AbortController: Strict Mode double-invokes effects in development.
    // Network-level GET deduping in `apiFetch` ensures we don't issue
    // redundant requests, while `requestIdRef` keeps state consistent.
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refetchToken, analyticsApiPath]);

  const refetch = useCallback(() => setRefetchToken((t) => t + 1), []);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}
